import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { userPreferencesService } from '@/services/userPreferencesService';
import type { MyCommissionsPreference } from '@/types/services';
import { myCommissionsPreferenceSchema } from '@/utils/serviceValidation';
import { createComponentLogger } from '@/utils/logger';

const defaults: MyCommissionsPreference = { columns: [], groupByMonth: false };
const log = createComponentLogger('CommissionRecordPreferences', 'reports');
const Context = createContext<{
  ready: boolean;
  value: MyCommissionsPreference;
  save?: (value: MyCommissionsPreference) => void;
}>({ ready: true, value: defaults });

/** One preference read per report visit; table-local state survives ordinary refreshes. */
export function CommissionRecordPreferences({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [value, setValue] = useState(defaults);
  const request = useRef<ReturnType<typeof userPreferencesService.getPreferences>>();
  useEffect(() => {
    let active = true;
    // Reuse the request through StrictMode's effect replay.
    request.current ??= userPreferencesService.getPreferences();
    void request.current.then(result => {
      if (active) {
        const parsed = myCommissionsPreferenceSchema.safeParse(result.success ? result.data.uiPreferences?.myCommissions : undefined);
        setValue(parsed.success ? parsed.data : defaults);
        if (!result.success) {
          log.warn('Could not load commission record preferences');
        }
        setReady(true);
      }
    }).catch(() => {
      if (active) {
        log.warn('Could not load commission record preferences');
        setReady(true);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const save = useCallback((next: MyCommissionsPreference) => {
    // Retain the complete selection, including columns hidden in an officer view,
    // for intentional tab/drill-in remounts during this visit.
    setValue(next);
    void userPreferencesService.updateUIPreferences({ myCommissions: next }).then(result => {
      if (!result.success) {
        log.warn('Could not save commission record preferences');
      }
    }).catch(() => log.warn('Could not save commission record preferences'));
  }, []);

  return <Context.Provider value={{ ready, value, save }}>{children}</Context.Provider>;
}

export const useCommissionRecordPreferences = () => useContext(Context);
